interface Author {
  uid: string | null,
  name: string,
  coverImage: string | null,
  isBot: boolean,
  isUser: boolean,
  isExpert: boolean,
}

/* eslint-disable no-underscore-dangle */
const botMessagesTypes = {
  welcome: 'bot:::welcome',
  requestName: 'bot:::requestName',
  requestCategory: 'bot:::requestCategory',
  predictionResult: 'bot:::predictionResult',
  predictionSelectionSmartForm: 'bot:::predictionSelectionSmartForm',
  predictionRoutingToExperts: 'bot:::predictionRoutingToExperts',
  requestPartnerSelection: 'bot:::requestPartnerSelection',
  requestEmail: 'bot:::requestEmail',
  connectingWithExpert: 'bot:::connectingWithExpert',
  thankForFillingForm: 'bot:::thankForFillingForm',
  connectionFailed: 'bot:::connectionFailed',
  transferContactInformationConfirmed: 'bot:::transferContactInformationConfirmed',
  requestVisitorRole: 'bot:::requestVisitorRole',
  smsFallbackRegistered: 'bot:::smsFallbackRegistered',
  waitFallbackRegistered: 'bot:::waitFallbackRegistered',
  requestEmailThanks: 'bot:::requestEmailThanks',
  requestSentSmartForm: 'bot:::requestSentSmartForm',
};

const botMessage = (
  id: string,
  text: string,
  type: string,
) => ({
  node: {
    id,
    author: {
      name: 'Guuru-Bot',
      coverImage: 'https://cdn.guuru.com/assets/logo/guuru_logo.svg',
      id: 'bot',
      isBot: true,
      photoUrl: 'https://cdn.guuru.com/assets/logo/guuru_logo.svg',
      uid: 'bot',
      __typename: 'BotAuthor',
    },
    isBot: true,
    isUser: false,
    text,
    i18n: text,
    type,
  },
});

const visitorMessage = (id: string | number, text: string) => ({
  node: {
    id,
    author: {
      id: 'bot',
      name: 'visitor',
      photoUrl: null,
      phone: null,
      __typename: 'UserAuthor',
    },
    isBot: false,
    isUser: true,
    text,
    createdAt: Date.now(),
    hideForExpert: false,
    hideForUser: false,
    metadata: {},
    type: 'text',
    __typename: 'TextMessage',
  },
});

const getAuthor = (oldAuthor: Author) => {
  let __typename = 'UserAuthor';

  if (oldAuthor.isBot) {
    __typename = 'BotAuthor';
  }
  if (oldAuthor.isExpert) {
    __typename = 'ExpertAuthor';
  }

  return ({
    __typename,
    photoUrl: oldAuthor.coverImage,
    id: oldAuthor.uid,
    ...oldAuthor,
  });
};

interface CreateMessageForPresentationProps {
  timestamp: number;
  i18n?: string;
  data: Record<string, any>;
  type: string;
  author: Author;
  id: string;
  message: string;
  address?: string;
  filename?: string;
  mimetype?: string;
}

const createMessageForPresentation = ({
  timestamp,
  i18n,
  data,
  type,
  author: oldAuthor,
  ...messageData
}: CreateMessageForPresentationProps) => {
  const author = getAuthor(oldAuthor);
  const {
    message,
    address,
    filename,
    mimetype,
    id,
  } = messageData;

  if (['smsFallback', 'waitFallback'].includes(type)) {
    return ({
      node: {
        ...messageData,
        type,
        createdAt: timestamp,
        author,
        text: i18n || message,
        metadata: data,
        phone: data.number,
        email: data.email,
      },
    });
  }

  if (type === 'contact') {
    return ({
      node: {
        ...messageData,
        type,
        createdAt: timestamp,
        author,
        text: i18n || message,
        metadata: data,
        attachment: {
          url: address,
          filename,
          mimetype,
        },
        user: {
          ...data.contact,
        },
      },
    });
  }

  if (type === 'attachment') {
    return ({
      node: {
        ...messageData,
        type,
        createdAt: timestamp,
        author,
        text: i18n || message,
        metadata: data,
        attachment: {
          url: address,
          filename,
          mimetype,
        },
      },
    });
  }

  if (type === 'waitTimeout') {
    return ({
      node: {
        id,
        type,
        createdAt: timestamp,
        author,
        text: i18n || message,
        metadata: data,
      },
    });
  }

  return ({
    node: {
      ...messageData,
      type,
      createdAt: timestamp,
      author,
      text: i18n || message,
      metadata: data,
    },
  });
};

export {
  createMessageForPresentation,
  botMessage,
  visitorMessage,
  botMessagesTypes,
};
